import { BACKEND_URL } from "lib/utils/baseUrl";
import { normalizeApiMessage } from "lib/i18n/apiMessage";
import { authFetch } from "./authFetch";
import { encodeNodeQrId } from "lib/utils/facilityQr";

function normalizeUploadMessage(payload, fallback, fallbackKey) {
  const normalized = normalizeApiMessage(payload, fallback);

  return {
    ...normalized,
    messageKey: /^(PROJECT_NODE_|INVALID_PROJECT_NODE_)/.test(payload?.code ?? "")
      ? "" : normalized.messageKey || fallbackKey || "",
  };
}

export async function postUdFileUpload(project_id, user_id, uploadedFiles, locale = "") {
  if (!user_id) {
    return {
      success: false,
      ...normalizeUploadMessage(
        { message: "Sign-in is required." },
        "Sign-in is required.",
        "api.auth.token.missing",
      ),
    };
  }

  const entries = Object.entries(uploadedFiles ?? {});

  const metadataPayload = {
    project_id,
    user_id,
    locale,
    files: entries.map(([, value], idx) => ({
      fileName: value.file.name,
      facility_type: value.groups[0],
      building_name: value.groups[1],
      floor: value.groups[2],
      location: value.groups[3],
      facility_name: value.groups[4],
      part_name: value.groups?.[5] ?? null,
      qr_id: encodeNodeQrId(value.node_id || value.qr_id) || value.qr_id || null,
      node_id: value.node_id ?? null,
      has_thumbnail: !!value.image_thumbnail,
      thumb_index: idx,
    })),
  };

  try {
    const formData = new FormData();

    Object.values(uploadedFiles ?? {}).forEach((value, idx) => {
      formData.append("files", value.file);

      if (value.image_thumbnail) {
        const thumbFile = dataURLtoFile(value.image_thumbnail, `thumb_${idx}.png`);
        formData.append("thumbnails", thumbFile);
      }
    });

    formData.append("metadata", JSON.stringify(metadataPayload));

    const fileRes = await authFetch(`${BACKEND_URL}/project/file_upload`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    const result = await fileRes.json().catch(() => null);

    if (!result) {
      return {
        success: false,
        ...normalizeUploadMessage(
          { message: "Failed to parse server response." },
          "Failed to parse server response.",
          "api.common.serverError",
        ),
      };
    }

    if (!fileRes.ok || result?.success !== true) {
      return {
        success: false,
        ...normalizeUploadMessage(
          result,
          "Upload failed.",
          "api.project.file.upload.failed",
        ),
        data: result?.data,
      };
    }

    return {
      success: true,
      ...normalizeUploadMessage(
        result,
        "Uploaded successfully.",
        "api.project.file.upload.success",
      ),
      data: result.data,
    };
  } catch (err) {
    console.error("Upload failed:", err);
    return {
      success: false,
      ...normalizeUploadMessage(
        { message: `Upload failed: ${err.message}` },
        `Upload failed: ${err.message}`,
        "api.common.networkError",
      ),
    };
  }
}

function dataURLtoFile(dataUrl, filename) {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}
